import { useEffect, useMemo } from 'react'
import { useParams, useNavigate, Navigate } from 'react-router-dom'
import { useSurvey } from '../store/surveyStore.js'
import { MIWAY_4, PERSON_DEMOGRAPHICS } from '../surveys/miway-4.js'
import SurveyShell from '../components/survey/SurveyShell.jsx'
import MultiSelectGrid from '../components/survey/MultiSelectGrid.jsx'
import DefinitionPick from '../components/survey/DefinitionPick.jsx'
import { useState } from 'react'

const TOTAL_STEPS = 26

// Step-Plan (1-indexiert wie im Original):
// 0  = Welcome (intro, kein Counter)
// 1  = Demographics
// 2  = Motivatoren-Auswahl
// 3..12  = Motivator-Definitionen (10×)
// 13 = Zusatz-Motivatoren
// 14 = Übergang
// 15 = Demotivatoren-Auswahl
// 16..25 = Demotivator-Definitionen (10×)
// 26 = Zusatz-Demotivatoren + Abschließen

export default function MiWayFlow() {
  const { accessId } = useParams()
  const nav = useNavigate()
  const survey = useSurvey()
  const [validation, setValidation] = useState(null)

  useEffect(() => {
    survey.load(accessId)
    return () => survey.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessId])

  if (survey.status === 'idle') return <div className="text-center py-12 text-ink-soft">Lade…</div>
  if (survey.status === 'completed') return <Navigate to={`/survey/${accessId}/result`} replace />

  const goNext = async () => {
    setValidation(null)
    const next = survey.step + 1
    if (next > TOTAL_STEPS) return
    survey.setStep(next)
    await survey.save()
  }
  const goBack = async () => {
    setValidation(null)
    if (survey.step <= 0) return
    survey.setStep(survey.step - 1)
    await survey.save()
  }

  const onFinish = async () => {
    setValidation(null)
    await survey.finish()
    nav(`/survey/${accessId}/result`)
  }

  const requireAndAdvance = (ok, message) => {
    if (!ok) { setValidation(message); return }
    goNext()
  }

  const step = survey.step
  const data = survey.data
  const copy = MIWAY_4.copy

  // ---- 0: Welcome ----
  if (step === 0) {
    return (
      <SurveyShell
        step={0} total={TOTAL_STEPS} hideBack
        title={MIWAY_4.welcome.headline}
        intro={null}
        onNext={goNext}
        nextLabel="Start"
      >
        <h2 className="text-xl text-ink-soft -mt-2 mb-4">{MIWAY_4.welcome.subheadline}</h2>
        {MIWAY_4.welcome.body.map((p, i) => (
          <p key={i} className="text-ink leading-relaxed mb-3">{p}</p>
        ))}
        <ol className="list-decimal pl-6 space-y-2 text-ink mt-4">
          {MIWAY_4.welcome.list.map((p, i) => <li key={i}>{p}</li>)}
        </ol>
      </SurveyShell>
    )
  }

  // ---- 1: Demographics ----
  if (step === 1) {
    const d = data.demographics
    return (
      <SurveyShell
        step={1} total={TOTAL_STEPS}
        title="Angaben zur Person"
        intro="Vervollständigen Sie bitte die Angaben zu Ihrer Person…"
        onBack={goBack}
        onNext={() => requireAndAdvance(d.organisation.trim().length > 0, copy.organisationRequired)}
        validation={validation}
      >
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Alter">
            <input className="input" value={d.alter} onChange={e => survey.setDemographics({ alter: e.target.value })} />
          </Field>
          <Field label={<>Derzeitige Organisation <span className="text-rose-500">*</span></>}>
            <input className={`input ${validation ? 'input-error' : ''}`} value={d.organisation} onChange={e => survey.setDemographics({ organisation: e.target.value })} />
          </Field>
          <Field label="Derzeitige Position">
            <input className="input" value={d.position} onChange={e => survey.setDemographics({ position: e.target.value })} />
          </Field>
          <Field label="Derzeitiger Bereich der Organisation">
            <select className="input" value={d.bereich} onChange={e => survey.setDemographics({ bereich: e.target.value })}>
              <option value="">Bitte auswählen…</option>
              {PERSON_DEMOGRAPHICS.bereich.map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Derzeitige Branche">
            <select className="input" value={d.branche} onChange={e => survey.setDemographics({ branche: e.target.value })}>
              <option value="">Bitte auswählen…</option>
              {PERSON_DEMOGRAPHICS.branche.map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Führungsverantwortung">
            <select className="input" value={d.fuehrungsverantwortung} onChange={e => survey.setDemographics({ fuehrungsverantwortung: e.target.value })}>
              <option value="">Bitte auswählen…</option>
              {PERSON_DEMOGRAPHICS.fuehrung.map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Höchster Bildungsabschluss">
            <select className="input" value={d.bildungsabschluss} onChange={e => survey.setDemographics({ bildungsabschluss: e.target.value })}>
              <option value="">Bitte auswählen…</option>
              {PERSON_DEMOGRAPHICS.bildung.map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Fachrichtung">
            <input className="input" placeholder="(z.B. Bauingenieurwesen, Soziologie)" value={d.fachrichtung} onChange={e => survey.setDemographics({ fachrichtung: e.target.value })} />
          </Field>
        </div>
      </SurveyShell>
    )
  }

  // ---- 2: Motivatoren-Auswahl ----
  if (step === 2) {
    return (
      <SurveyShell
        step={2} total={TOTAL_STEPS}
        title={copy.motivatorTitle}
        intro={copy.motivatorIntro}
        onBack={goBack}
        onNext={() => requireAndAdvance(
          data.motivators.selected.length === 10,
          'Bitte wählen Sie genau 10 Motivatoren aus.'
        )}
        validation={validation}
      >
        <MultiSelectGrid
          options={MIWAY_4.motivators.map(m => m.label)}
          selected={data.motivators.selected}
          onToggle={(label) => survey.toggleSelected('motivators', label, 10)}
          max={10}
          counterTemplate={copy.motivatorCounter}
        />
      </SurveyShell>
    )
  }

  // ---- 3..12: Motivator Definitionen ----
  if (step >= 3 && step <= 12) {
    return <DefinitionStep
      kind="motivators" copy={copy} step={step}
      onBack={goBack} onNext={goNext} validation={validation} setValidation={setValidation}
    />
  }

  // ---- 13: Zusatz-Motivatoren ----
  if (step === 13) {
    return (
      <SurveyShell step={13} total={TOTAL_STEPS} title={copy.motivatorTitle} intro={copy.motivatorAdditional} onBack={goBack} onNext={goNext}>
        <NumberedTextList kind="motivators" />
      </SurveyShell>
    )
  }

  // ---- 14: Übergang ----
  if (step === 14) {
    return (
      <SurveyShell step={14} total={TOTAL_STEPS} title="" intro={copy.transition} onBack={goBack} onNext={goNext}>
        <div className="text-center text-ink-soft">→</div>
      </SurveyShell>
    )
  }

  // ---- 15: Demotivatoren-Auswahl ----
  if (step === 15) {
    return (
      <SurveyShell
        step={15} total={TOTAL_STEPS}
        title={copy.demotivatorTitle}
        intro={copy.demotivatorIntro}
        onBack={goBack}
        onNext={() => requireAndAdvance(
          data.demotivators.selected.length === 10,
          'Bitte wählen Sie genau 10 Demotivatoren aus.'
        )}
        validation={validation}
      >
        <MultiSelectGrid
          options={MIWAY_4.demotivators.map(m => m.label)}
          selected={data.demotivators.selected}
          onToggle={(label) => survey.toggleSelected('demotivators', label, 10)}
          max={10}
          counterTemplate={copy.demotivatorCounter}
        />
      </SurveyShell>
    )
  }

  // ---- 16..25: Demotivator Definitionen ----
  if (step >= 16 && step <= 25) {
    return <DefinitionStep
      kind="demotivators" copy={copy} step={step}
      onBack={goBack} onNext={goNext} validation={validation} setValidation={setValidation}
    />
  }

  // ---- 26: Zusatz-Demotivatoren + Abschließen ----
  if (step === 26) {
    return (
      <SurveyShell
        step={26} total={TOTAL_STEPS}
        title={copy.demotivatorTitle}
        intro={copy.demotivatorAdditional}
        onBack={goBack}
        onNext={onFinish}
        nextLabel="Abschließen"
        nextVariant="primary"
      >
        <NumberedTextList kind="demotivators" />
      </SurveyShell>
    )
  }

  return <div>Unbekannter Schritt: {step}</div>
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
    </div>
  )
}

function NumberedTextList({ kind }) {
  const survey = useSurvey()
  const items = survey.data[kind].additional
  return (
    <div className="space-y-3">
      {items.map((v, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="w-7 h-7 rounded-full bg-teal-100 text-teal-800 grid place-items-center text-sm font-semibold shrink-0">
            {i + 1}
          </span>
          <input className="input" value={v} onChange={e => survey.setAdditional(kind, i, e.target.value)} />
        </div>
      ))}
    </div>
  )
}

function DefinitionStep({ kind, copy, step, onBack, onNext, validation, setValidation }) {
  const survey = useSurvey()
  const data = survey.data
  const baseStep = kind === 'motivators' ? 3 : 16
  const itemIndex = step - baseStep
  const word = data[kind].selected[itemIndex]
  const list = kind === 'motivators' ? MIWAY_4.motivators : MIWAY_4.demotivators
  const item = useMemo(() => list.find(m => m.label === word), [word, list])

  if (!item) {
    return (
      <SurveyShell step={step} total={TOTAL_STEPS} title="" onBack={onBack} onNext={onNext}>
        <div className="text-rose-700 text-sm">
          Es konnte kein Begriff für diesen Schritt gefunden werden — vermutlich wurden weniger als 10 Begriffe ausgewählt.
        </div>
      </SurveyShell>
    )
  }

  const priorities = data[kind].definitions[word] || [null, null]
  const filled = priorities.filter(x => x != null).length

  const validationMsg = kind === 'motivators' ? copy.motivatorDefValidation : copy.demotivatorDefValidation
  const intro = kind === 'motivators' ? copy.motivatorDefIntro : copy.demotivatorDefIntro
  const title = kind === 'motivators' ? copy.motivatorTitle : copy.demotivatorTitle

  return (
    <SurveyShell
      step={step} total={TOTAL_STEPS}
      title={title} intro={intro}
      onBack={onBack}
      onNext={() => {
        if (filled < 2) { setValidation(validationMsg); return }
        setValidation(null); onNext()
      }}
      validation={validation}
    >
      <DefinitionPick
        word={word} kind={kind} defs={item.defs}
        priorities={priorities} copy={copy}
        onAssign={(prioIdx, defIdx) => survey.setDefinition(kind, word, prioIdx, defIdx)}
        onRemove={(prioIdx) => survey.removeDefinition(kind, word, prioIdx)}
      />
    </SurveyShell>
  )
}
